import { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { MapPin, Mail, Phone, User, Compass } from "lucide-react";

export default function Zones() {
  const [items, setItems] = useState([]);

  useEffect(() => { api.get("/zones").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  return (
    <div data-testid="zones-page">
      <PageHero
        eyebrow="Federation Zones"
        title="Zones & Zonal Heads of NKF Bharat"
        subtitle="The National Karate Federation – NKF Bharat operates through regional zones led by appointed Zonal Heads who oversee state units, coach networks and championships within their zone."
      />

      <section className="section-spacing nkf-container">
        {items.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-10 text-center">
            <Compass className="h-10 w-10 text-slate-400 mx-auto" />
            <div className="font-display text-xl text-nkf-blue mt-3">Zonal directory will be published here</div>
            <p className="text-sm text-slate-500 mt-2">The federation administrator will publish official zones and appointed zonal heads shortly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {items.map((z) => (
              <article key={z.id} data-testid={`zone-${z.id}`} className="federation-card bg-white border border-slate-200 rounded-md overflow-hidden flex flex-col h-full">
                <div className="bg-nkf-blue text-white px-5 py-4 flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-nkf-gold shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-nkf-gold">Zone</div>
                    <div className="font-display text-xl leading-tight truncate">{z.name}</div>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start gap-2.5">
                    <User className="h-4 w-4 text-nkf-red mt-1 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-display text-base text-nkf-blue truncate">
                        {z.zonal_head_name || "To be appointed"}
                      </div>
                      {z.designation && (
                        <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold">{z.designation}</div>
                      )}
                    </div>
                  </div>

                  {z.states_covered && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">States / Regions</div>
                      <div className="text-sm text-slate-700 mt-1 leading-relaxed">{z.states_covered}</div>
                    </div>
                  )}

                  {z.description && (
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">{z.description}</p>
                  )}

                  {(z.contact_email || z.contact_phone) && (
                    <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5 text-sm">
                      {z.contact_email && (
                        <a href={`mailto:${z.contact_email}`} className="flex items-center gap-2 text-slate-600 hover:text-nkf-blue">
                          <Mail className="h-3.5 w-3.5 text-nkf-gold" /> {z.contact_email}
                        </a>
                      )}
                      {z.contact_phone && (
                        <a href={`tel:${z.contact_phone}`} className="flex items-center gap-2 text-slate-600 hover:text-nkf-blue">
                          <Phone className="h-3.5 w-3.5 text-nkf-gold" /> {z.contact_phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-12 p-6 md:p-8 bg-nkf-navy text-white rounded-md flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Compass className="h-7 w-7 text-nkf-gold shrink-0" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold">Zonal Governance</div>
              <div className="font-display text-2xl mt-1">Interested in joining a zone or hosting a zonal event?</div>
              <div className="text-white/70 text-sm mt-1">Reach out to your Zonal Head directly, or contact the federation secretariat for further coordination.</div>
            </div>
          </div>
          <a href="/contact" className="bg-nkf-gold text-nkf-navy px-5 py-2.5 rounded text-sm uppercase tracking-widest font-bold hover:bg-white transition">Contact Federation</a>
        </div>
      </section>
    </div>
  );
}
